import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { TRANSFORMATIONS } from "./constants";

export function DisconnectDialog({ open, onOpenChange, onConfirm }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="disconnect-dialog">
        <DialogHeader>
          <DialogTitle>Scollegare JOBTIMEHR?</DialogTitle>
          <DialogDescription>
            La disconnessione interromperà le future sincronizzazioni e rimuoverà le credenziali.
            I dati ESG già acquisiti resteranno nello storico.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="disconnect-cancel">Annulla</Button>
          <Button className="bg-red-700 hover:bg-red-800" onClick={onConfirm} data-testid="disconnect-confirm">Scollega</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function RotateDialog({ open, onOpenChange, rotateForm, onRotateFormChange, onSubmit }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="rotate-dialog">
        <DialogHeader>
          <DialogTitle>Aggiorna credenziali</DialogTitle>
          <DialogDescription>Le nuove credenziali vengono testate prima del salvataggio. Mapping e storico restano invariati.</DialogDescription>
        </DialogHeader>
        <form onSubmit={onSubmit} className="space-y-4">
          <div className="space-y-1.5">
            <Label>Nuovo Client ID *</Label>
            <Input value={rotateForm.client_id} onChange={(e) => onRotateFormChange({ ...rotateForm, client_id: e.target.value })} required data-testid="rotate-client-id" />
          </div>
          <div className="space-y-1.5">
            <Label>Nuovo Client Secret *</Label>
            <Input type="password" value={rotateForm.client_secret} onChange={(e) => onRotateFormChange({ ...rotateForm, client_secret: e.target.value })} required data-testid="rotate-client-secret" />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} data-testid="rotate-cancel">Annulla</Button>
            <Button type="submit" className="bg-green-700 hover:bg-green-800" data-testid="rotate-submit">Testa e aggiorna</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function SyncDetailDialog({ syncDetail, onClose }) {
  return (
    <Dialog open={!!syncDetail} onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="sync-detail-dialog">
        <DialogHeader>
          <DialogTitle>Dettaglio sincronizzazione</DialogTitle>
          <DialogDescription>Nessun secret viene mai mostrato.</DialogDescription>
        </DialogHeader>
        {syncDetail && (
          <dl className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
            <div><dt className="text-xs text-slate-500">Fonte</dt><dd className="font-medium">{syncDetail.adapter}{syncDetail.mock ? " (MOCK)" : ""}</dd></div>
            <div><dt className="text-xs text-slate-500">Mode</dt><dd className="font-medium">{syncDetail.mode || "FULL"}</dd></div>
            <div><dt className="text-xs text-slate-500">Stato</dt><dd className="font-medium">{syncDetail.status}</dd></div>
            <div><dt className="text-xs text-slate-500">Periodo richiesto</dt><dd className="font-medium">{syncDetail.date_from || "—"} → {syncDetail.date_to || "—"}</dd></div>
            <div><dt className="text-xs text-slate-500">Record ricevuti</dt><dd className="font-medium">{syncDetail.received ?? "—"}</dd></div>
            <div><dt className="text-xs text-slate-500">Metriche valide</dt><dd className="font-medium">{syncDetail.valid ?? "—"}</dd></div>
            <div><dt className="text-xs text-slate-500">KPI aggiornati</dt><dd className="font-medium">{syncDetail.kpi_updated ?? "—"}</dd></div>
            <div><dt className="text-xs text-slate-500">Conflitti</dt><dd className="font-medium">{syncDetail.conflicts_count ?? 0}</dd></div>
            <div className="col-span-2"><dt className="text-xs text-slate-500">Domini saltati</dt>
              <dd className="font-medium">{(syncDetail.skipped || []).map((s) => `${s.domain} (${s.reason})`).join(", ") || "—"}</dd></div>
            <div className="col-span-2"><dt className="text-xs text-slate-500">Errori</dt>
              <dd className="font-medium text-red-700">{(syncDetail.errors || []).map((e) => `${e.domain}: ${e.kind}`).join(", ") || syncDetail.error || "—"}</dd></div>
            <div className="col-span-2"><dt className="text-xs text-slate-500">Avvisi</dt>
              <dd className="text-xs text-slate-600">{(syncDetail.warnings || []).join(" ") || "—"}</dd></div>
          </dl>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function MappingDialog({ open, onOpenChange, mapForm, onMapFormChange, onSubmit, catalog }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="mapping-dialog">
        <DialogHeader>
          <DialogTitle>Nuova regola di mapping</DialogTitle>
          <DialogDescription>Campo sorgente → trasformazione → metrica canonica.</DialogDescription>
        </DialogHeader>
        <form onSubmit={onSubmit} className="space-y-4">
          <div className="space-y-1.5">
            <Label>Sistema sorgente *</Label>
            <Input value={mapForm.source_system} onChange={(e) => onMapFormChange({ ...mapForm, source_system: e.target.value })} required data-testid="mapping-source-system" />
          </div>
          <div className="space-y-1.5">
            <Label>Campo sorgente *</Label>
            <Input value={mapForm.source_field} onChange={(e) => onMapFormChange({ ...mapForm, source_field: e.target.value })} required placeholder="es. employment_type" data-testid="mapping-source-field" />
          </div>
          <div className="space-y-1.5">
            <Label>Trasformazione *</Label>
            <Select value={mapForm.transformation} onValueChange={(v) => onMapFormChange({ ...mapForm, transformation: v })}>
              <SelectTrigger data-testid="mapping-transformation"><SelectValue /></SelectTrigger>
              <SelectContent>{TRANSFORMATIONS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Metrica canonica *</Label>
            <Select value={mapForm.canonical_metric} onValueChange={(v) => onMapFormChange({ ...mapForm, canonical_metric: v })}>
              <SelectTrigger data-testid="mapping-canonical-metric"><SelectValue placeholder="Seleziona" /></SelectTrigger>
              <SelectContent>{catalog.map((m) => <SelectItem key={m.metric_code} value={m.metric_code}>{m.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} data-testid="mapping-cancel">Annulla</Button>
            <Button type="submit" className="bg-green-700 hover:bg-green-800" data-testid="mapping-submit">Crea mapping</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
